/**
 * EmptyState — `Oprappo Design System.dc.html § 07 অবস্থা · খালি অবস্থা`.
 *
 * Frame note: "An empty state names what will fill it and offers one way forward. No
 * illustrations — a drawing of an empty box tells the user nothing they did not already
 * know." There is deliberately no illustration or icon slot.
 *
 * Two forms are drawn, and they differ in more than the button:
 *   with action    `padding:24px 16px`, title `600 17px/1.75`, button 48px at `margin-top:15px`
 *   without action `padding:20px 16px`, title `600 16px/1.75`
 * Both: `border:1px solid #E2DED5`, centred, body `400 14px/1.75` muted at `margin-top:5px`.
 * The coupling of padding and title size to the presence of an action is transcribed as
 * drawn — see README § deviations.
 */
import { Button } from './Button.js';
import { cx } from './internal/cx.js';

export interface EmptyStateProps {
  /** Bangla headline naming what is missing, e.g. `এখনও কোনো লেনদেন নেই`. */
  titleBn: string;
  /** Bangla line naming what will fill it, e.g. `প্রথম রিচার্জ বা বিল দিলে এখানে দেখা যাবে।`. */
  bodyBn?: string | undefined;
  /** Bangla label of the one way forward, e.g. `রিচার্জ করুন`. */
  actionLabelBn?: string | undefined;
  onAction?: (() => void) | undefined;
  className?: string | undefined;
}

export function EmptyState({
  titleBn,
  bodyBn,
  actionLabelBn,
  onAction,
  className,
}: EmptyStateProps): JSX.Element {
  const hasAction = actionLabelBn !== undefined;

  return (
    <div
      className={cx(
        'border border-border text-center bg-white',
        hasAction ? 'px-[16px] py-[24px]' : 'px-[16px] py-[20px]',
        className,
      )}
    >
      <div
        className={cx(
          'font-anek font-semibold leading-[1.75] text-ink',
          hasAction ? 'text-[17px]' : 'text-[16px]',
        )}
      >
        {titleBn}
      </div>
      {bodyBn !== undefined ? (
        <div className="font-anek font-normal text-[14px] leading-[1.75] text-muted mt-[5px]">{bodyBn}</div>
      ) : null}
      {hasAction ? (
        <div className="mt-[15px]">
          <Button tone="primary" size="md" onClick={onAction}>
            {actionLabelBn}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
